import { usePermissao } from "@/contexts/autorizacao/permissao";
import { ImpressaoAnaliseLaboratorial } from "@/contexts/laboratorio/analiseLaboratorial/components/ImpressaoAnaliseLaboratorial/ImpressaoAnaliseLaboratorial";
import { ModalCancelarAnaliseLaboratorial } from "@/contexts/laboratorio/analiseLaboratorial/components/ModalCancelarAnaliseLaboratorial/ModalCancelarAnaliseLaboratorial";
import { useAnaliseLaboratorial } from "@/contexts/laboratorio/analiseLaboratorial/hooks/useAnaliseLaboratorial";
import { AnaliseLaboratorial } from "@/contexts/laboratorio/analiseLaboratorial/model/AnaliseLaboratorial";
import { PaginaAnaliseLaboratorial } from "@/contexts/laboratorio/analiseLaboratorial/model/PaginaAnaliseLaboratorial";
import { env } from "@/env";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import {
  CloseOutlined,
  DownloadOutlined,
  EyeOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { App, Button, List, Space, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useReactToPrint } from "react-to-print";

export function ItemListFiltroRequisicao({
  item,
  reloadItems,
}: {
  item: PaginaAnaliseLaboratorial["content"][number];
  reloadItems: () => void;
}) {
  const { isOnline } = useNetworkStatus();
  const solicitacaoRef = useRef<HTMLDivElement>(null);
  const { getAnaliseLaboratorialByIdApi, addRequisicao, listaRequisicoes } =
    useAnaliseLaboratorial();
  const { hasEnabledPermissao } = usePermissao();
  const { notification, message } = App.useApp();
  const navigate = useNavigate();
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [isOpenModalCancelar, setIsOpenModalCancelar] = useState(false);
  const [requisicao, setRequisicao] = useState<AnaliseLaboratorial | null>(
    null
  );

  const redirectToView = () => {
    navigate(`/analises-laboratoriais/visualizar/${item.id}`);
  };

  const handlePrint = useReactToPrint({
    contentRef: solicitacaoRef,
    documentTitle: "requisicao_analise_laboratorial",
    suppressErrors: true,
    preserveAfterPrint: true,
    pageStyle: "@page{size:A4 !important; }",
  });

  const handleDownload = async () => {
    try {
      if (listaRequisicoes.find((r) => r.id === item.id)) {
        notification.info({
          message: "Esta Requisição já foi baixada localmente.",
          key: "info",
        });
        return;
      }

      message.loading({
        content: "Baixando requisição...",
        key: "loading",
        duration: 0,
      });
      setIsLoadingDownload(true);

      const requisicao = await getAnaliseLaboratorialByIdApi(item.id!);
      setIsLoadingDownload(false);
      message.destroy("loading");

      addRequisicao(requisicao);
      notification.success({
        message: "Requisição baixada com sucesso!",
        key: "success",
      });
    } catch (error) {
      console.error("Erro ao baixar a requisição:", error);
      message.destroy("loading");
      setIsLoadingDownload(false);
      const messageError = formatMessageError(error);
      notification.error({
        message: " Não foi possível baixar a requisição",
        key: "error",
        description: messageError,
      });
    }
  };

  const handleOpenModalCancelar = () => {
    setIsOpenModalCancelar(true);
  };

  const handleMountRequisicao = async () => {
    const requisicao = await getAnaliseLaboratorialByIdApi(item.id!);
    setRequisicao(requisicao);
  };

  const produtoDesc = item.produto
    ? `${item.produto?.produto?.nome} ${item.produto?.numeroRotulo}`
    : "Água";

  useEffect(() => {
    if (requisicao) {
      handlePrint();
    }
  }, [requisicao, handlePrint]);

  return (
    <>
      <List.Item
        extra={
          <Space>
            <Tooltip title="Baixar Para uso de campo" destroyOnHidden>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                disabled={
                  isLoadingDownload ||
                  !isOnline ||
                  item.situacao === "CANCELADO"
                }
                loading={isLoadingDownload}
              />
            </Tooltip>

            <Tooltip title="Imprimir" destroyOnHidden>
              <Button
                size="small"
                icon={<PrinterOutlined />}
                onClick={handleMountRequisicao}
                disabled={!isOnline || item.situacao === "CANCELADO"}
              />
            </Tooltip>

            <Tooltip title="Visualizar" destroyOnHidden>
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={redirectToView}
                disabled={
                  !isOnline ||
                  item.situacao === "CANCELADO" ||
                  !hasEnabledPermissao("RAL_VISUALIZAR")
                }
              />
            </Tooltip>

            <Tooltip title="Cancelar" destroyOnHidden>
              <Button
                size="small"
                danger
                icon={<CloseOutlined />}
                onClick={handleOpenModalCancelar}
                disabled={
                  !isOnline ||
                  item.situacao === "CANCELADO" ||
                  !hasEnabledPermissao("RAL_CANCELAR")
                }
              />
            </Tooltip>
          </Space>
        }
      >
        <List.Item.Meta
          title={
            <Typography.Text
              strong
              style={{
                color: isOpenModalCancelar ? "#d7263d" : undefined,
                opacity: item.situacao === "CANCELADO" ? 0.6 : 1,
              }}
            >
              Requisição {item.id}{" "}
              {item.situacao === "CANCELADO" ? "(Cancelada)" : ""}
            </Typography.Text>
          }
          description={
            <Typography.Text
              style={{
                color: isOpenModalCancelar ? "#d7263d" : "#8c8c8c",
                fontSize: 12,
              }}
            >
              Produto: {produtoDesc}
              <br />
              Data Coleta: {dayjs(item.dataColeta).format("DD/MM/YYYY HH:mm")}
            </Typography.Text>
          }
        />
      </List.Item>

      <div style={{ display: "none" }}>
        <ImpressaoAnaliseLaboratorial
          marcadagua={env.REACT_APP_AMBIENTE}
          ref={solicitacaoRef}
          item={requisicao}
        />
      </div>

      <ModalCancelarAnaliseLaboratorial
        requisicaoId={item.id!}
        isOpen={isOpenModalCancelar}
        onClose={() => {
          setIsOpenModalCancelar(false);
        }}
        reloadItems={reloadItems}
      />
    </>
  );
}
